import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { BrokerPortalFeatureCodes, ProductTag, RenewableSystemType } from '@brokerportal/common/enums';
import { FeatureFlagService } from '@brokerportal/common/services/feature-flag.service';
import { Subscription } from 'rxjs';
import { HEUF_SYSTEM_TYPES, STANDARD_SYSTEM_TYPES, SystemTypeOption } from './renewable-system-type-options';

export const BP_RENEWABLE_SYSTEM_TYPES_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RenewableSystemTypesComponent),
    multi: true
};

const AIRCON_OPTION: SystemTypeOption = { value: RenewableSystemType.Aircon, label: 'Aircon', icon: 'ac_unit' };

@Component({
    selector: 'bp-renewable-system-types',
    templateUrl: './renewable-system-types.component.html',
    styleUrls: ['./renewable-system-types.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [BP_RENEWABLE_SYSTEM_TYPES_VALUE_ACCESSOR],
    host: {
        class: 'bp-renewable-system-types',
        '[attr.aria-disabled]': 'disabled'
    }
})
export class RenewableSystemTypesComponent implements OnInit, AfterViewInit, ControlValueAccessor {
    @Input() name: string;
    @Input() productTag: ProductTag;
    @Input()
    public set allowedSystemTypes(value: RenewableSystemType[] | null | undefined) {
        this._allowedSystemTypes = value && value.length > 0 ? [...value] : null;
        this.refreshOptions();
    }
    public get allowedSystemTypes(): RenewableSystemType[] | null {
        return this._allowedSystemTypes;
    }
    @Input()
    public set value(value: string | null | undefined) {
        if (!this.viewInitialized) {
            this.initialValue = value;
        } else {
            this.setSelectionByValue(value);
            this.valueChange.emit(value);
        }
    }
    public get value() {
        return this.getValue();
    }
    @Input()
    public set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
    }
    public get disabled() {
        return this._disabled;
    }
    private _disabled = false;

    @Output('change') readonly valueChange = new EventEmitter<string>();
    @ViewChild('standardControl') private standardControl?: MatButtonToggleGroup;

    private touched = false;
    private initialValue: string | null | undefined;
    private viewInitialized = false;

    private heufAssetsSubscription: Subscription;
    private airconForInterestFreeSubscription: Subscription;

    private useHEUFAssetsForGreen = false;
    private enableAirconForInterestFree = false;
    private _allowedSystemTypes: RenewableSystemType[] | null = null;
    private _options: ReadonlyArray<SystemTypeOption> = STANDARD_SYSTEM_TYPES;

    get rawValue() {
        return this.standardControl?.value as string[] | undefined;
    }

    get options(): ReadonlyArray<SystemTypeOption> {
        return this._options;
    }

    constructor(private cdr: ChangeDetectorRef, private featureFlagService: FeatureFlagService) {}

    ngOnInit() {
        this.heufAssetsSubscription = this.featureFlagService
            .isEnabled(BrokerPortalFeatureCodes.HEUFAssetsForGreen)
            .subscribe(enabled => {
                this.useHEUFAssetsForGreen = enabled;
                this.refreshOptions();
            });

        this.airconForInterestFreeSubscription = this.featureFlagService
            .isEnabled(BrokerPortalFeatureCodes.InterestFreeAirconAsset)
            .subscribe(enabled => {
                this.enableAirconForInterestFree = enabled;
                this.refreshOptions();
            });
    }

    ngAfterViewInit() {
        this.viewInitialized = true;
        if (this.initialValue !== undefined) {
            this.setSelectionByValue(this.initialValue);
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy() {
        if (this.heufAssetsSubscription) {
            this.heufAssetsSubscription.unsubscribe();
        }
        if (this.airconForInterestFreeSubscription) {
            this.airconForInterestFreeSubscription.unsubscribe();
        }
    }

    private onChange = (_: any) => {};
    private onTouched = () => {};

    //#region ControlValueAccessor methods
    writeValue(obj: string | null | undefined) {
        this.value = obj;
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    markAsTouched() {
        if (!this.touched) {
            this.touched = true;
            this.onTouched();
        }
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }
    //#endregion ControlValueAccessor methods

    _emitValueChange() {
        const currentValue = this.getValue();
        this.onChange(currentValue);
        this.valueChange.emit(currentValue);
        this.markAsTouched();
    }

    private refreshOptions() {
        let newOptions: SystemTypeOption[] = [...STANDARD_SYSTEM_TYPES];

        if (this.useHEUFAssetsForGreen && this.productTag === ProductTag.GreenLoan) {
            HEUF_SYSTEM_TYPES.forEach(heufOpt => {
                if (!newOptions.some(opt => opt.value === heufOpt.value)) {
                    newOptions.push(heufOpt);
                }
            });
        }

        if (this.enableAirconForInterestFree && this.productTag === ProductTag.RenewableInterestFree) {
            if (!newOptions.some(opt => opt.value === AIRCON_OPTION.value)) {
                newOptions.push(AIRCON_OPTION);
            }
        }

        if (this.allowedSystemTypes && this.allowedSystemTypes.length > 0) {
            newOptions = newOptions.filter(opt => this.allowedSystemTypes.includes(opt.value));
        }

        this._options = Object.freeze([...newOptions]);

        if (this.viewInitialized) {
            this.setSelectionByValue(this.getValue());
            this.cdr.detectChanges();
        }
    }

    private setSelectionByValue(value: string | null | undefined) {
        if (this.standardControl) {
            const availableValues = new Set(this._options.map(opt => opt.value));
            const values = (value || '').split(',').filter(v => v && availableValues.has(v as RenewableSystemType));
            this.standardControl.value = values.length > 0 ? values : null;
        }
    }

    private getValue(): string | undefined {
        const availableValues = new Set(this._options.map(opt => opt.value));
        const selectedValues = this.standardControl?.value as string[] | undefined;
        if (selectedValues && selectedValues.length > 0) {
            const validSelectedValues = selectedValues.filter(v => v && availableValues.has(v as RenewableSystemType));
            if (validSelectedValues.length > 0) {
                return validSelectedValues.join(',');
            }
        }
        return undefined;
    }
}
